import modes from './modes'
import useStore from './store'
import * as THREE from 'three'
import * as Tone from "tone"
import ReactDOM from 'react-dom'
import React, { Suspense, useRef, useEffect, useState, useMemo, useCallback } from 'react'
import { OrbitControls, MeshWobbleMaterial } from '@react-three/drei'
import { Canvas, useLoader, useFrame, useThree } from 'react-three-fiber'
import lerp from 'lerp'
import './styles.css'
import { TweenMax, Sine, Expo, gsap, Back } from "gsap"


Tone.Transport.bpm.value = 70

const PolysWrapper = () => {
  const matcap = useLoader(THREE.TextureLoader, './512/C7C7D7_4C4E5A_818393_6C6C74-512px.png')

  const { shape, mut } = useStore(state => state)
  const timeout = useRef()
  const [show, setShow] = useState(false);
  const [material, setMaterial] = useState(false)
  const [shapeOut, setShapeOut] = useState(0);

  useEffect(() => {
    mut.tweenRef2 && mut.tweenRef2.kill()
    mut.tweenRef2 = TweenMax.to(mut, mut.delay, {
      scale: 0, ease: Sine.easeOut, onComplete: () => {
        setShow(false)
        setShapeOut(shape)
        setTimeout(() => {
          setShow(true)
          TweenMax.to(mut, mut.delay, { scale: 1, ease: Sine.easeOut })
        }, 50)
      }
    })
  }, [shape])

  useEffect(() => {
    let material = new THREE.MeshMatcapMaterial({ matcap: matcap })
    setMaterial(material)
  }, [matcap])
  return (
    show && <Polys shape={shapeOut} material={material} />
  )
}

function Polys({ shape, material }) {
  const amt = useStore(state => state.amt)
  const shapes = useStore(state => state.shapes)
  const mut = useStore(state => state.mut)
  const set = useStore(state => state.api.set)
  const isMobile = useStore(state => state.isMobile)
  const mesh = useRef()
  const polys = useRef()
  const geoRef = useRef()
  const dummy = useMemo(() => new THREE.Object3D(), [])

  useEffect(() => {
    mut.polysRef = polys.current
  }, [polys])
  let speed = 0.02;
  useFrame(state => {
    let t = state.clock.getElapsedTime();
    const intersects = []
    mut.cursors.forEach(cursor => {
      intersects.push(...cursor.intersects)
    });
    mut.data.forEach((part, i) => {
      let fract = ((i + 1) / amt)
      let { pos, rot } = part
      pos.x = lerp(pos.x, modes[mut.mode].position.x(t, i, amt, fract), speed)
      pos.y = lerp(pos.y, modes[mut.mode].position.y(t, i, amt, fract), speed)
      pos.z = lerp(pos.z, modes[mut.mode].position.z(t, i, amt, fract), speed)
      rot.x = lerp(rot.x, modes[mut.mode].rotation.x(t, i, amt, fract), speed)
      rot.y = lerp(rot.y, modes[mut.mode].rotation.y(t, i, amt, fract), speed)
      rot.z = lerp(rot.z, modes[mut.mode].rotation.z(t, i, amt, fract), speed)
      rot.z = lerp(rot.z, modes[mut.mode].rotation.z(t, i, amt, fract), speed)
      part.scale = lerp(
        part.scale,
        intersects.includes(i) ? modes[mut.mode].scale(t, i, amt, fract) * 1.5 : modes[mut.mode].scale(t, i, amt, fract),
        intersects.includes(i) ? 0.4 : 0.02
      )
      dummy.position.set(
        pos.x,
        pos.y,
        pos.z
      )
      dummy.rotation.set(
        rot.x,
        rot.y,
        rot.z
      )
      dummy.scale.setScalar(part.scale * mut.scale)
      dummy.updateMatrix()
      mesh.current.setMatrixAt(i, dummy.matrix)
    })
    mesh.current.instanceMatrix.needsUpdate = true
  })
  const out = (e) => { mut.hoovering = -1 }
  const down = (e) => {
    let mode = randomExcluded(0, modes.length - 1, mut.mode)
    mut.mode = mode
    set('mode', mode);
  }
  return (
    <group ref={polys}>
      <instancedMesh renderOrder={1} ref={mesh} args={[null, null, amt]} onPointerOut={out} onPointerDown={down}>
        <bufferGeometry ref={geoRef} attach="geometry" {...shapes[shape]} />
        <meshMatcapMaterial depthTest={false} attach="material" {...material} />
      </instancedMesh>
    </group>
  )
}

const Ui = () => {
  const { set } = useStore(state => state.api)
  const { mode, mut, tone, shape, shapes, cursorsAmt } = useStore(state => state)

  const clickedMode = (i) => {
    mut.mode = i;
    set('mode', i);
  }
  const clickedShape = (i) => {
    set('shape', i);
  }
  const clickedLoop = (i) => {
    mut.cursors[i].active.map((v, i) => {
      tone.inst[v].triggerRelease()
    })
    mut.cursors.splice(i, 1)
    set('cursorsAmt', mut.cursors.length)
    mut.overCursor = -1
  }
  const overLoop = (over, i) => {
    if (over) {
      mut.overCursor = i
    } else {
      mut.overCursor = -1
    }
  }
  tone.init()
  return (
    <>
      <span className="modesTitle">MACRO</span>
      <div className="modes">
        {new Array(modes.length).fill().map((v, i) => <div key={i} className={"mode " + (mode === i ? 'active' : '')} onClick={() => clickedMode(i)} />)}
      </div>
      <span className="shapesTitle">MICRO</span>
      <div className="shapes">
        {new Array(shapes.length).fill().map((v, i) => <div key={i} className={"shape " + (shape === i ? 'active' : '')} onClick={() => clickedShape(i)} />)}
      </div>
      <span className="loopsTitle">LOOPS</span>
      <div className="loops">
        {new Array(cursorsAmt).fill().map((v, i) => <div onPointerOver={() => overLoop(true, i)} onPointerOut={() => overLoop(false, i)} key={i} className={"loop " + (shape === i ? 'active' : '')} onClick={() => clickedLoop(i)} ></div>)}
        {cursorsAmt === 0 ? 'click & drag' : ''}
      </div>
    </>
  )
}

const Cursors = () => {
  const { cursorsAmt } = useStore(state => state)
  return (
    <group>
      {new Array(cursorsAmt).fill().map((v, i) => <Cursor key={i} i={i} />)}
    </group>
  )
}

function Cursor({ i }) {
  const { camera, mouse } = useThree();
  const magnet = useRef();
  const track = useRef();
  const wobble = useRef();
  const cursor = useStore(state => state.cursor)
  const trackGeo = useStore(state => state.trackGeo)
  const mut = useStore(state => state.mut)
  const set = useStore(state => state.api.set)
  var vec = new THREE.Vector3();
  var vec2 = new THREE.Vector3();
  var pos = new THREE.Vector3();
  var pos2 = new THREE.Vector3();

  useFrame(state => {
    if (mut.pointerDown) {
      let curr = mut.cursors[mut.cursors.length - 1]
      curr.modX = (curr.x - mouse.x)
      curr.modY = (curr.y - mouse.y)
    }

    vec.set(
      mut.cursors[i].mod.x,
      mut.cursors[i].mod.y,
      0.5
    );
    vec.unproject(camera);
    vec.sub(camera.position).normalize(); 
    var distance = - camera.position.z / vec.z;
    pos.copy(camera.position).add(vec.multiplyScalar(distance));
    magnet.current.position.copy(pos);
    
    vec2.set(
      mut.cursors[i].x,
      mut.cursors[i].y,
      0.5
    );
    vec2.unproject(camera);
    vec2.sub(camera.position).normalize();
    var distance2 = - camera.position.z / vec2.z;
    pos2.copy(camera.position).add(vec2.multiplyScalar(distance2));
    track.current.position.copy(pos2);
    let s = 5.225
    mut.cursors[i].size = lerp(mut.cursors[i].size, 1, 0.02)
    let magnetScale = mut.overCursor === i ? 4 : Math.max(Math.abs(mut.cursors[i].modY) + Math.abs(mut.cursors[i].modX)*s*0.2,0.5)*mut.cursors[i].size
    magnet.current.scale.set(
      magnetScale,magnetScale,magnetScale
    )
    track.current.scale.set(
      Math.abs(mut.cursors[i].modX)*s*camera.aspect, 
      Math.abs(mut.cursors[i].modY)*s, 
      1
    )
   track.current.material.color.set(mut.overCursor === i ? '#111' : '#eee')
   mut.cursors[i].wobble = lerp(mut.cursors[i].wobble, 0, 0.02)
   wobble.current.factor = mut.cursors[i].wobble

  });
  return (
    <>
      <mesh ref={track} rotation={[0,Math.PI, 0]} renderOrder={0} geometry={trackGeo} >
        <MeshWobbleMaterial depthWrite={false} attach="material" ref={wobble} speed={2} factor={0} />
      </mesh>
      <mesh ref={magnet} renderOrder={2} geometry={cursor}>
        <meshStandardMaterial depthTest={false} attach="material" color="#030303" />
      </mesh>
    </>
  );
}

const ThreeStuff = () => {
  const { camera, mouse } = useThree()
  const ray = useStore(state => state.ray)
  const set = useStore(state => state.api.set)
  const tone = useStore(state => state.tone)
  const angle = useStore(state => state.angle)
  const angles = useStore(state => state.angles)
  const mut = useStore(state => state.mut)
  const amt = useStore(state => state.amt)
  useEffect(() => {
    camera.position.copy(new THREE.Vector3(angles[angle].position.x,angles[angle].position.y,angles[angle].position.z));
    camera.lookAt(0, 0, 0)
    set('camera', camera)
  }, [camera])
  let scales = ["C", "D", 'E', 'F', 'G', 'A']
  let getNote = (i) => {
    if(i < amt/2){
      return scales[i % (scales.length)] + (Math.floor((i) / scales.length)+2)
    }else {
      let j = amt - i
      return scales[j % (scales.length)] + (Math.floor((j) / scales.length)+2)
    }
  }
  useFrame(state => {
    if (!mut.polysRef) return
    let t = state.clock.getElapsedTime()
    mut.cursors.forEach(cursor => {
      cursor.mod.x = cursor.x + Math.sin(t) * cursor.modX
      cursor.mod.y = cursor.y + Math.cos(t) * cursor.modY

      ray.setFromCamera(cursor.mod, camera)
      let intersects = ray.intersectObjects(mut.polysRef.children, true).map((i) => i.instanceId)
      let activate = intersects.filter(x => cursor.intersects.indexOf(x) === -1);
      activate.map((i) => {
        cursor.active.push(i)
        let note = getNote(i)
        cursor.wobble = Math.min(cursor.wobble + (Tone.Frequency(note) * 0.1),1) 
        cursor.size = Math.min(cursor.size + 3, 8) 
        tone.inst[i].triggerAttack(note, "+0.01", 0.1 + (Math.random() * 0.1))
      })
      cursor.active.map((v, i) => {
        if (intersects.indexOf(v) === -1) {
          cursor.active.splice(i, 1)
          //tone.inst[v].triggerRelease()
        }
      })
      cursor.intersects = intersects
    });
    mut.mouse.x = mouse.x
    mut.mouse.y = mouse.y
    mut.mouse.x !== 0 && (mut.started = true)
  })
  return (
    <>
      <group>
        <Suspense fallback={null}>
          <Cursors />
          <PolysWrapper />
          <ambientLight intensity={20} />
        </Suspense>
      </group>
    </>
  )
}
// <OrbitControls /> 
const App = () => {
  const mut = useStore(state => state.mut)
  const set = useStore(state => state.api.set)
  const down = (e) => {
    mut.pointerDown = true
    set('pointerDown', true)
    mut.cursors.push(
      { x: mut.mouse.x, y: mut.mouse.y, modX: 0, modY: 0, active: [], intersects: [], mod: {}, wobble: 0, size: 1 },
    )
    set('cursorsAmt', mut.cursors.length)
  }
  const up = () => {
    mut.pointerDown = false
    set('pointerDown', false)
  }
  return (
    <>
      <Ui />
      <div className="bg" />
      <Canvas onPointerDown={down} onPointerUp={up} pixelRatio={1.25} gl={{ antialias: true }} camera={{ position: [0, 0, -7.5], fov: 70, near: 0.1, far: 200 }}>
        <ThreeStuff />
      </Canvas>
    </>
  )
}

ReactDOM.render(
  <App />,
  document.getElementById('root')
)

function randomExcluded(min, max, excluded) {
  var n = Math.floor(Math.random() * (max - min) + min);
  if (n >= excluded) n++;
  return n;
}
console.log('%c🌱🌱 @ramioso 2021-05-05 🌱🌱 ', 'color: orange');

